import { Field, FieldProps as FormikFieldProps } from 'formik'
import { useMemo } from 'react'
import { colors } from '~/styles/colors'

import { FieldProps } from '../field'
import { PopoverMenu } from '../popover-menu'
import { SelectMenuButton } from '../select/style'
import { Meta } from '../typography'
import IconDropdown from './assets/icon-dropdown.svg'

export type SelectProps = FieldProps & {
  value: string[]
  options: Array<{ label: string; value: string }>
  onSelect: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
}

export const MultiSelect = ({
  options,
  value,
  onSelect,
  placeholder,
  disabled,
  ...field
}: SelectProps) => {
  const items = options.map(option => {
    return (
      <PopoverMenu.Item
        key={String(option.value)}
        onClick={() => {
          const index = value.findIndex(v => v === option.value)
          if (index === -1) {
            onSelect([...value, option.value])
          } else {
            value.splice(index, 1)
            onSelect([...value])
          }
        }}
        selected={value.includes(option.value)}
        label={option.label}
        closeOnSelect={false}
      />
    )
  })

  return (
    <PopoverMenu
      button={
        <SelectMenuButton
          {...field}
          disabled={disabled}
          icon={<IconDropdown />}
        >
          <Meta regular color={colors.dark}>
            {value.length === 0 ? placeholder : `${value.length} selected`}
          </Meta>
        </SelectMenuButton>
      }
      items={items}
    />
  )
}

type SelectFieldProps = FieldProps & {
  name: string
  options: SelectProps['options']
  placeholder?: string
  disabled?: boolean
  autoComplete?: string
}

export const MultiSelectField = ({ name, ...rest }: SelectFieldProps) => {
  return (
    <Field name={name}>
      {({ field, meta, form }: FormikFieldProps) => {
        return (
          <MultiSelect
            value={field.value}
            onSelect={value => form.setFieldValue(name, value)}
            error={meta.touched && meta.error ? meta.error : undefined}
            {...rest}
          />
        )
      }}
    </Field>
  )
}

export const useSelectOptions = <T,>(
  data: T[] | undefined,
  mapper: (item: T) => SelectProps['options'][0]
): SelectProps['options'] => {
  return useMemo(() => (!data ? [] : data.map(mapper)), [data])
}
